<template>
  <a-page
    :id="app_id"
    :image_source="image_source"
    :image_height="image_height"
  >
    <template v-slot:header_options></template>
    <template v-if="is_all_hotels">
      <a-hotel-selector-page></a-hotel-selector-page>
    </template>
    <template v-else>
      <template v-if="loading">
        <v-card class="mt-2">
          <v-card-text>
            <!-- Tarjetas de contenido -->
            <v-row>
              <v-col cols="12" md="6">
                <a-card>
                  <v-skeleton-loader
                    type="article, actions"
                  ></v-skeleton-loader>
                  <v-skeleton-loader
                    type="table-heading, list-item-two-line, image, table-tfoot"
                  ></v-skeleton-loader>
                </a-card>
              </v-col>
              <v-col cols="12" md="6">
                <a-card>
                  <v-skeleton-loader
                    type="article, actions"
                  ></v-skeleton-loader>
                  <v-skeleton-loader
                    type="table-heading, list-item-two-line, image, table-tfoot"
                  ></v-skeleton-loader>
                </a-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </template>
      <template v-else>
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <a-card :title="$t('restaurant_production')" class="px-3">
                <v-row>
                  <!-- Panel de Producción Manual -->
                  <v-col cols="12" md="6">
                    <a-card :title="$t('add_manual_production')" class="px-3">
                      <v-alert
                        v-if="has_data"
                        type="warning"
                        text
                        class="text-center"
                      >
                        <h3 class="mb-2">
                          {{ $t("date_already_has") }}
                          {{ formatMoney(revenue_already_inserted) }}
                          {{ $t("net_revenue") }}
                        </h3>
                        <p>
                          {{ $t("data_import_already_done") }}<br />{{
                            $t("you_can_delete_them")
                          }}
                        </p>
                        <v-btn color="warning" small @click="deleteDay">
                          {{ $t("delete_day_data") }}
                        </v-btn>
                      </v-alert>

                      <v-form ref="form" v-model="validForm">
                        <v-menu
                          ref="dateMenu"
                          v-model="dateMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="form_data.date_formatted"
                              :label="$t('date')"
                              prepend-icon="mdi-calendar"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              :rules="dateRules"
                              required
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="form_data.date_raw"
                            @input="dateMenu = false"
                            locale="es"
                          ></v-date-picker>
                        </v-menu>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="form_data.total_revenue"
                              :label="$t('total_amount')"
                              :rules="revenueRules"
                              required
                              :hint="$t('must_be_positive_value')"
                              persistent-hint
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-switch
                              v-model="form_data.includes_tax"
                              :label="$t('contains_iva')"
                              class="mt-0"
                            ></v-switch>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model.number="form_data.total_customers"
                              :label="$t('diners')"
                              type="number"
                              min="1"
                              :rules="positiveNumberRules"
                              required
                              :hint="$t('must_be_positive_number')"
                              persistent-hint
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model.number="form_data.total_tickets"
                              :label="$t('tickets')"
                              type="number"
                              min="1"
                              :rules="positiveNumberRules"
                              required
                              :hint="$t('must_be_positive_number')"
                              persistent-hint
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="form_data.group"
                              :items="availableGroups"
                              item-text="text"
                              item-value="value"
                              :label="$t('group')"
                              :rules="selectRules"
                              required
                            ></v-select>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="form_data.product_id"
                              :items="availableProducts"
                              item-text="name"
                              item-value="id"
                              :label="$t('product')"
                              :loading="loading"
                              :rules="productRules"
                              required
                              :disabled="!form_data.group"
                            ></v-select>
                          </v-col>
                        </v-row>
                        <v-btn
                          color="secondary"
                          class="mt-3"
                          rounded
                          @click="saveForm"
                          :disabled="!validForm"
                          :loading="loading"
                        >
                          <v-icon small>mdi-plus</v-icon>
                          {{ $t("insert_production") }}
                        </v-btn>
                      </v-form>
                    </a-card>
                  </v-col>

                  <!-- Panel de Importación Masiva -->
                  <v-col cols="12" md="6">
                    <a-card :title="$t('add_massive_production')" class="px-3">
                      <p>
                        {{ $t("restaurant_massive_import_description") }}
                      </p>
                      <ol>
                        <li>
                          {{
                            $t("restaurant_massive_import_description_list_1")
                          }}
                          <a
                            href="/templates/Template_Import_Revenue_Restaurant.xlsx"
                            target="_blank"
                          >
                            "{{ $t("download_example_template") }}"
                          </a>
                        </li>
                        <li>
                          {{
                            $t("restaurant_massive_import_description_list_2")
                          }}
                        </li>
                        <li>
                          {{
                            $t("restaurant_massive_import_description_list_3")
                          }}
                        </li>
                        <li>
                          {{
                            $t("restaurant_massive_import_description_list_4")
                          }}
                        </li>
                        <li>
                          {{
                            $t("restaurant_massive_import_description_list_5")
                          }}
                        </li>
                        <li>
                          {{
                            $t("restaurant_massive_import_description_list_6")
                          }}
                        </li>
                      </ol>

                      <v-file-input
                        v-model="uploadFile"
                        :label="$t('select_file')"
                        class="mt-8"
                        accept=".xlsx"
                        rounded
                        outlined
                        depressed
                        show-size
                      ></v-file-input>

                      <v-switch
                        v-model="removeBeforeUpload"
                        :label="$t('delete_existing_data')"
                        class="mt-0"
                      ></v-switch>

                      <v-btn
                        color="secondary"
                        class="mt-3"
                        rounded
                        @click="startImport"
                        :disabled="!uploadFile || isUploading"
                        :loading="isUploading"
                      >
                        <v-icon left>mdi-cloud-upload</v-icon>
                        {{ $t("import_excel_data") }}
                      </v-btn>

                      <v-alert
                        v-if="upload_task.task"
                        :type="upload_taskType"
                        text
                        class="mt-5"
                      >
                        <h4 class="mb-2">{{ upload_taskTitle }}</h4>
                        <p>{{ upload_taskParagraph }}</p>
                        <div
                          v-if="
                            upload_task.task.status === 'processing' ||
                              upload_task.task.status === 'pending'
                          "
                          class="d-flex align-center"
                        >
                          <v-progress-circular
                            indeterminate
                            color="secondary"
                            size="20"
                            class="mr-2"
                          ></v-progress-circular>
                          <p class="mb-0">{{ upload_taskMessage }}</p>
                        </div>
                      </v-alert>
                    </a-card>
                  </v-col>
                </v-row>
              </a-card>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </template>
  </a-page>
</template>

<script>
import aCard from "@/components/aCard.vue";
import aPage from "@/components/aPage";
import aHotelSelectorPage from "@/components/selector/aHotelSelectorPage.vue";
import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "RestaurantSales",
  components: {
    aHotelSelectorPage,
    aPage,
    aCard
  },
  data: () => ({
    app_id: "restaurant-sales-app",
    image_source: "/img/header-2.jpg", // Mandatory path
    image_height: 300, // Mandatory number
    loading: false,
    validForm: false,
    dateMenu: false,
    form_data: {
      date_formatted: "", // Formato "DD/MM/YYYY" para mostrar
      date_raw: "", // Formato ISO "YYYY-MM-DD" para v-date-picker
      date: "", // La fecha en formato "DD/MM/YYYY" para enviar a la API
      includes_tax: false,
      total_revenue: "0,00",
      total_tickets: 1,
      total_customers: 1,
      group: "food",
      product_id: 0
    },
    uploadFile: null,
    fileErrorMessages: [],
    removeBeforeUpload: true,
    isUploading: false
  }),
  computed: {
    // Reglas de validación como propiedades computadas
    dateRules() {
      return [
        value => !!value || this.$t("required_field"),
        value =>
          /^\d{2}\/\d{2}\/\d{4}$/.test(value) || this.$t("date_format_error")
      ];
    },

    revenueRules() {
      return [
        value => !!value || this.$t("required_field"),
        value => {
          const numValue = parseFloat(value.replace(/,/g, "."));
          return (
            (!isNaN(numValue) && numValue > 0) || this.$t("must_be_positive")
          );
        }
      ];
    },

    positiveNumberRules() {
      return [
        value => !!value || this.$t("required_field"),
        value =>
          (typeof value === "number" && value > 0) ||
          this.$t("must_be_positive")
      ];
    },

    selectRules() {
      return [value => !!value || this.$t("select_option")];
    },

    productRules() {
      return [
        value => !!value || this.$t("select_option"),
        value => value > 0 || this.$t("select_valid_option")
      ];
    },

    ready() {
      return this.current_hotel && this.current_hotel.id;
    },

    loaded() {
      return this.current_hotel != null;
    },

    is_all_hotels() {
      return this.current_hotel && this.current_hotel.children_number > 0;
    },

    // Grupos de productos disponibles
    availableGroups() {
      if (!this.restaurant_products) return [];
      return Object.keys(this.restaurant_products || {}).map(group => ({
        text: this.$t(group),
        value: group
      }));
    },

    // Productos filtrados por grupo seleccionado
    availableProducts() {
      if (!this.restaurant_products)
        return [{ id: 0, name: this.$t("loading_products") }];
      const groupProducts =
        this.restaurant_products[this.form_data.group] || [];
      return [{ id: 0, name: this.$t("select_product") }, ...groupProducts];
    },

    // Información de tarea de importación
    upload_taskType() {
      if (!this.upload_task) return "info";
      return this.upload_task.task.status === "error" ? "error" : "info";
    },

    upload_taskTitle() {
      if (!this.upload_task.task) return "";

      switch (this.upload_task.task.status) {
        case "done":
          return this.$t("imported_data");
        case "error":
          return this.$t("error_importing_file");
        case "pending":
          return this.$t("importing_data");
        case "processing":
          return this.$t("importing_data");
        default:
          return this.$t("import");
      }
    },

    upload_taskParagraph() {
      if (!this.upload_task.task) return "";
      switch (this.upload_task.task.status) {
        case "done":
          return `${this.$t("days_imported_successfully")} ${this.upload_task
            .task_childs_done || 0} ${this.$t("days")} ${this.$t(
            "at"
          )} ${this.formatDate(this.upload_task.end_datetime)}`;
        case "error":
          return this.$t("error_occurred_importing");
        case "pending":
          return this.$t("importing_data_file_upload");
        case "processing":
          return this.$t("importing_data_file_upload");
        default:
          return "";
      }
    },

    upload_taskMessage() {
      if (!this.upload_task.task) return "";

      switch (this.upload_task.task.status) {
        case "pending":
          return this.$t("preparing_import");
        case "processing":
          return `${this.$t("imported")} ${this.upload_task.task_childs_done ||
            0} ${this.$t("days")}. ${this.$t("days_left")} ${this.upload_task
            .task_childs_pending || 0} ${this.$t("days_to_import")}`;
        default:
          return "";
      }
    },

    // Mapeo de estado desde la store
    ...mapState({
      restaurant_products: state => state.restaurant.products,
      revenue_already_inserted: state =>
        state.restaurant.revenue_already_inserted,
      has_data: state => state.restaurant.has_data,
      upload_task: state => state.restaurant.upload_task
    }),

    // Mapeo de getters desde la store
    ...mapGetters({
      current_hotel: "current_hotel"
    })
  },
  methods: {
    // Método para obtener los datos del restaurante
    async getRestaurantData() {
      if (!this.current_hotel || this.current_hotel.id == null) {
        return;
      }

      try {
        // Cargar productos disponibles
        await this.getRestaurantProducts({ hotel_id: this.current_hotel.id });
        // Cargar datos para la fecha seleccionada
        await this.loadRestaurantRevenueDate({
          hotel_id: this.current_hotel.id,
          date: this.form_data.date_formatted
        });
      } finally {
        this.loading = false;
      }
    },

    // Método para cargar datos de un día específico
    async loadRevenueDate() {
      if (!this.current_hotel || !this.form_data.date_formatted) return;

      try {
        this.loading = true;
        await this.loadRestaurantRevenueDate({
          hotel_id: this.current_hotel.id,
          date: this.form_data.date_formatted
        });
      } finally {
        this.loading = false;
      }
    },

    // Métodos para el formulario de inserción manual
    saveForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;

      this.saveRestaurantRevenueDay({
        hotel_id: this.current_hotel.id,
        modify: 0,
        date: this.form_data.date_formatted,
        total_revenue: this.form_data.total_revenue,
        includes_tax: this.form_data.includes_tax ? 1 : 0,
        total_customers: this.form_data.total_customers,
        total_tickets: this.form_data.total_tickets,
        group: this.form_data.group,
        product_id: this.form_data.product_id
      })
        .then(() => {
          this.loadRevenueDate();
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Método para formatear dinero
    formatMoney(value) {
      if (!value) return "";
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2
      }).format(value);
    },

    // Utilizamos moment para el manejo de fechas
    refreshFormData() {
      // Obtenemos el día anterior
      const yesterday = Vue.moment().subtract(1, "days");

      // Formato raw para el date-picker: YYYY-MM-DD
      const rawDate = yesterday.format("YYYY-MM-DD");

      // Formato para mostrar y enviar a la API: DD/MM/YYYY
      const formattedDate = yesterday.format("DD/MM/YYYY");

      this.form_data = {
        date_formatted: formattedDate,
        date_raw: rawDate,
        date: formattedDate, // Compatible con código anterior
        includes_tax: false,
        total_revenue: "0,00",
        total_tickets: 1,
        total_customers: 1,
        group: "food",
        product_id: 0
      };

      // Resetear validación
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      // Cargar datos para la nueva fecha
      this.loadRevenueDate();
    },

    deleteDay() {
      if (!this.current_hotel || !this.form_data.date_formatted) return;

      this.loading = true;

      this.deleteRestaurantRevenueDay({
        hotel_id: this.current_hotel.id,
        date: this.form_data.date_formatted
      }).finally(() => {
        this.loadRevenueDate();
        this.loading = false;
      });
    },

    async startImport() {
      if (!this.uploadFile || !this.current_hotel) return;

      const formData = new FormData();
      formData.append("file", this.uploadFile);
      formData.append("hotel_id", this.current_hotel.id);
      formData.append("remove_before", this.removeBeforeUpload);

      try {
        this.loading = true;
        await this.uploadRestaurantDataFile({
          formData
        });

        this.createInterval();

        // La acción ya se encarga de gestionar el estado de la tarea
        this.uploadFile = null;
      } finally {
        this.loading = false;
      }
    },

    createInterval() {
      return setInterval(async () => {
        await this.getRestaurantDataFileStatus({
          hotel_id: this.current_hotel.id
        });
      }, 5000);
    },

    async init() {
      this.getRestaurantData();
      await this.getRestaurantDataFileStatus({
        hotel_id: this.current_hotel.id
      });
      if (this.upload_task.task) {
        this.createInterval();
      }
    },

    formatDate(date) {
      return Vue.moment(date).format("HH:mm - DD/MM/YYYY");
    },

    // Mapeo de acciones desde la store
    ...mapActions({
      getRestaurantProducts: "getRestaurantProducts",
      loadRestaurantRevenueDate: "loadRestaurantRevenueDate",
      saveRestaurantRevenueDay: "saveRestaurantRevenueDay",
      deleteRestaurantRevenueDay: "deleteRestaurantRevenueDay",
      uploadRestaurantDataFile: "uploadRestaurantDataFile",
      getRestaurantDataFileStatus: "getRestaurantDataFileStatus"
    })
  },
  watch: {
    ready(v) {
      if (v) {
        this.init();
      }
    },

    // Actualizar el formato cuando cambia el date-picker
    "form_data.date_raw": function(newVal) {
      if (newVal) {
        // Usar moment para convertir la fecha
        this.form_data.date_formatted = Vue.moment(newVal).format("DD/MM/YYYY");
        this.form_data.date = this.form_data.date_formatted; // Compatible con código anterior
        this.loadRevenueDate();
      }
    },

    "form_data.group": function() {
      // Reiniciar la selección de producto cuando cambia el grupo
      this.form_data.product_id = 0;

      // Forzar revalidación del selector de productos
      if (this.$refs.form) {
        this.$nextTick(() => {
          this.$refs.form.validate();
        });
      }
    }
  },
  mounted() {
    this.loading = true;
    this.refreshFormData();
    if (this.ready) {
      this.init();
    }
  }
};
</script>
